import moment from "moment";
import { useCallback, useEffect, useRef, useState } from "react";
import type { StudentTestContent } from "p6m-p6u";
import { Views } from "../enums/views";
import { convertTimeLimitToSeconds, getReadableTimeFromSeconds } from "../helpers/DateTime";

interface UseCountdownParams {
    rehydrated: boolean;
    teacherTest: StudentTestContent;
    currentMainView: Views | number;
    countdownStarted: boolean;
    setCountdownStarted: (val: boolean) => void;
    testEndTimestamp: number;
    setTestEndTimestamp: (val: number) => void;
    setCountdownStartedAt: (val: number) => void;
    /**
     * Owner preview: the timer still renders so the author sees what students see, but it never
     * auto-submits. Without this, previewing a test whose deadline has passed ends the preview on
     * the first tick — the owner is bounced back out before the exercises appear.
     */
    isPreview: boolean;
    onTimeOver: () => void;
}

const TIME_RUNNING_OUT_THRESHOLD_SECONDS = 180;
const DEADLINE_ONLY_VISIBLE_THRESHOLD_SECONDS = 6 * 3600;

const useCountdown = (params: UseCountdownParams) => {
    const [isTimeOver, setIsTimeOver] = useState(false);
    const [timeRemaining, setTimeRemaining] = useState<string | undefined>(undefined);
    const [isTimeRunningOut, setIsTimeRunningOut] = useState(false);
    const [shouldBounceTimer, setShouldBounceTimer] = useState(false);
    const hasAutoSubmittedRef = useRef(false);
    const hasTriggeredBounceRef = useRef(false);

    const hasTimeLimit = !!params.teacherTest.timeLimit || !!params.teacherTest.dueDate;

    useEffect(() => {
        if (params.currentMainView === Views.EXERCISES && hasTimeLimit && !params.countdownStarted) {
            params.setCountdownStarted(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.currentMainView, hasTimeLimit, params.countdownStarted]);

    useEffect(() => {
        if (params.rehydrated && params.countdownStarted && params.testEndTimestamp === 0) {
            const startedAt = moment().unix();
            const timeLimitEnd = params.teacherTest.timeLimit
                ? startedAt + convertTimeLimitToSeconds(params.teacherTest.timeLimit)
                : null;
            const dueDateEnd = params.teacherTest.dueDate ? Math.floor(params.teacherTest.dueDate / 1000) : null;

            const candidates = [timeLimitEnd, dueDateEnd].filter((t): t is number => t !== null);
            if (candidates.length > 0) {
                params.setCountdownStartedAt(startedAt);
                params.setTestEndTimestamp(Math.min(...candidates));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.rehydrated, params.countdownStarted, params.teacherTest]);

    useEffect(() => {
        if (!params.testEndTimestamp) return;

        const updateCountdown = () => {
            const secondsLeft = params.testEndTimestamp - moment().unix();

            const hasOnlyDeadline = !params.teacherTest.timeLimit && !!params.teacherTest.dueDate;
            const shouldHideTimer = hasOnlyDeadline && secondsLeft > DEADLINE_ONLY_VISIBLE_THRESHOLD_SECONDS;
            setTimeRemaining(shouldHideTimer ? undefined : getReadableTimeFromSeconds(Math.max(0, secondsLeft)));
            const timeRunningOut = secondsLeft > 0 && secondsLeft <= TIME_RUNNING_OUT_THRESHOLD_SECONDS;
            setIsTimeRunningOut(timeRunningOut);

            if (timeRunningOut && !hasTriggeredBounceRef.current) {
                hasTriggeredBounceRef.current = true;
                setShouldBounceTimer(true);
            }

            if (
                secondsLeft <= 0 &&
                !hasAutoSubmittedRef.current &&
                params.currentMainView === Views.EXERCISES &&
                !params.isPreview
            ) {
                hasAutoSubmittedRef.current = true;
                setIsTimeOver(true);
                params.onTimeOver();
            }
        };

        updateCountdown();
        const interval = !hasAutoSubmittedRef.current ? setInterval(updateCountdown, 1000) : undefined;

        return () => {
            if (interval) clearInterval(interval);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.testEndTimestamp, params.currentMainView, params.isPreview]);

    const clearBounce = useCallback(() => setShouldBounceTimer(false), []);

    return { isTimeOver, timeRemaining, isTimeRunningOut, shouldBounceTimer, clearBounce };
};

export default useCountdown;
